import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { Card, Button, Badge } from '@shared/components/ui';
import {
  TrendingUp,
  Users,
  DollarSign,
  UserMinus,
  Download,
  BarChart2,
  PieChart,
  Globe,
  Database,
} from 'lucide-react';

interface AnalyticsData {
  customer_growth: {
    month: string;
    new_customers: number;
    total_customers: number;
  }[];
  revenue_trends: {
    month: string;
    mrr: number;
    revenue: number;
    growth_rate: number;
  }[];
  churn_rate: {
    monthly_churn_rate: number;
    churned_customers: number;
    total_customers: number;
  };
  feature_usage: {
    feature_name: string;
    usage_count: number;
    unique_users: number;
  }[];
  geographic_distribution: {
    country: string;
    customer_count: number;
    percentage: number;
  }[];
  top_backup_destinations: {
    destination_name: string;
    storage_used: number;
    backup_count: number;
    organization_count: number;
    percentage: number;
  }[];
}

export default function PlatformAnalytics() {
  const { t, i18n } = useTranslation('admin');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const token = getToken();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/admin/analytics?range=12m', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setAnalytics(await res.json());
        setError(null);
      } else if (res.status === 403) {
        setError('access_denied_platform_admin_privileges_required');
      } else {
        setError('failed_to_load_analytics_data');
      }
    } catch {
      setError('failed_to_connect_to_server');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (exporting) return;
    setExportError(false);
    setExporting(true);
    try {
      const res = await fetch(`/api/v1/admin/analytics/export?format=${exportFormat}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `platform-analytics.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else setExportError(true);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <>
        <div
          role="status"
          aria-label={t('common:status.loading')}
          className="py-20 flex justify-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </>
    );
  }

  if (error || !analytics) {
    return (
      <>
        <div role="alert" className="py-20 text-center space-y-4">
          <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
            {t(
              error === 'access_denied_platform_admin_privileges_required'
                ? 'platformAnalytics.accessDeniedTitle'
                : 'platformAnalytics.title'
            )}
          </h2>
          <p className="text-text-muted-light dark:text-text-muted-dark">
            {error ? t(error) : t('failed_to_load_analytics_data')}
          </p>
          <Button onClick={() => void fetchAnalytics()}>{t('common:actions.retry')}</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        {exportError && (
          <p
            role="alert"
            className="rounded-xl p-4 text-state-danger-light bg-state-danger-bg-light dark:text-state-danger-dark dark:bg-state-danger-bg-dark"
          >
            {t('failed_to_export_analytics')}
          </p>
        )}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="app-page-title">{t('platformAnalytics.title')}</h1>
            <p className="app-page-subtitle">{t('platformAnalytics.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3 bg-surface-card-light dark:bg-surface-card-dark p-1 rounded-xl shadow-sm border border-surface-border-light dark:border-surface-border-dark">
            <select
              aria-label={t('platformAnalytics.exportFormat')}
              value={exportFormat}
              disabled={exporting}
              onChange={(e) => setExportFormat(e.target.value as any)}
              className="app-field-control min-w-28"
            >
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
            </select>
            <Button size="sm" onClick={handleExport} isLoading={exporting}>
              <Download size={16} className="mr-2" /> {t('platformAnalytics.export')}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-6">
          <StatCard
            label={t('platformAnalytics.totalCustomers')}
            value={analytics.customer_growth?.slice(-1)[0]?.total_customers || 0}
            icon={<Users className="w-6 h-6" />}
            color="text-state-info-light dark:text-state-info-dark"
            bg="bg-state-info-bg-light dark:bg-state-info-bg-dark"
          />
          <StatCard
            label="MRR"
            value={new Intl.NumberFormat(i18n.resolvedLanguage || 'en', {
              style: 'currency',
              currency: 'USD',
            }).format(analytics.revenue_trends?.slice(-1)[0]?.mrr || 0)}
            icon={<DollarSign className="w-6 h-6" />}
            color="text-state-ok-light dark:text-state-ok-dark"
            bg="bg-state-ok-bg-light dark:bg-state-ok-bg-dark"
          />
          <StatCard
            label={t('platformAnalytics.monthlyChurn')}
            value={formatPercent(
              analytics.churn_rate?.monthly_churn_rate || 0,
              i18n.resolvedLanguage || 'en',
              2
            )}
            icon={<TrendingUp className="w-6 h-6" />}
            color="text-state-warn-light dark:text-state-warn-dark"
            bg="bg-state-warn-bg-light dark:bg-state-warn-bg-dark"
          />
          <StatCard
            label={t('platformAnalytics.churnedUsers')}
            value={analytics.churn_rate?.churned_customers || 0}
            icon={<UserMinus className="w-6 h-6" />}
            color="text-state-danger-light dark:text-state-danger-dark"
            bg="bg-state-danger-bg-light dark:bg-state-danger-bg-dark"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customer Growth */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                <BarChart2 className="text-brand-600 dark:text-brand-400" />{' '}
                {t('platformAnalytics.customerGrowth')}
              </h3>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar pr-2">
              {(analytics.customer_growth || []).length === 0 && (
                <AnalyticsEmptyState message={t('platformAnalytics.noData')} />
              )}
              {(analytics.customer_growth || []).map((data) => (
                <div
                  key={data.month}
                  className="flex items-center justify-between p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl hover:bg-surface-card-light dark:hover:bg-surface-card-dark transition-colors"
                >
                  <div>
                    <p className="font-bold text-slate-900 dark:text-white">
                      {formatMonth(data.month, i18n.resolvedLanguage || 'en')}
                    </p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-bold uppercase tracking-wider">
                      {t('platformAnalytics.totalCount', { count: data.total_customers })}
                    </p>
                  </div>
                  <Badge variant="success">
                    +{t('platformAnalytics.newCount', { count: data.new_customers })}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>

          {/* Revenue Trends */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                <DollarSign className="text-state-ok-light dark:text-state-ok-dark" />{' '}
                {t('platformAnalytics.revenueTrends')}
              </h3>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar pr-2">
              {(analytics.revenue_trends || []).length === 0 && (
                <AnalyticsEmptyState message={t('platformAnalytics.noData')} />
              )}
              {(analytics.revenue_trends || []).map((data) => (
                <div
                  key={data.month}
                  className="flex items-center justify-between p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl hover:bg-surface-card-light dark:hover:bg-surface-card-dark transition-colors"
                >
                  <div>
                    <p className="font-bold text-slate-900 dark:text-white">
                      {formatMonth(data.month, i18n.resolvedLanguage || 'en')}
                    </p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-bold uppercase tracking-wider">
                      {t('platformAnalytics.mrr')}:{' '}
                      {new Intl.NumberFormat(i18n.resolvedLanguage || 'en', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(data.mrr)}
                    </p>
                  </div>
                  <Badge variant={data.growth_rate >= 0 ? 'success' : 'error'}>
                    {data.growth_rate >= 0 ? '+' : ''}
                    {formatPercent(data.growth_rate, i18n.resolvedLanguage || 'en', 2)}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Feature Usage */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                <PieChart className="text-accent-text-light dark:text-accent-text-dark" />{' '}
                {t('platformAnalytics.featureUsage')}
              </h3>
            </div>
            <div className="space-y-3">
              {(analytics.feature_usage || []).length === 0 && (
                <AnalyticsEmptyState message={t('platformAnalytics.noData')} />
              )}
              {(analytics.feature_usage || []).map((feature, idx) => (
                <div
                  key={feature.feature_name}
                  className="flex items-center justify-between p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-black text-text-subtle-light dark:text-text-subtle-dark">
                      #{idx + 1}
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {formatFeatureName(feature.feature_name, t)}
                      </p>
                      <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('platformAnalytics.userCount', { count: feature.unique_users })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-brand-600 dark:text-brand-400">
                      {formatNumber(feature.usage_count, i18n.resolvedLanguage || 'en')}
                    </span>
                    <span className="text-xs text-text-subtle-light dark:text-text-subtle-dark ml-1 font-bold uppercase">
                      {t('platformAnalytics.useCount', { count: feature.usage_count })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Backup Destinations */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                <Database className="text-state-info-light dark:text-state-info-dark" />{' '}
                {t('platformAnalytics.topDestinations')}
              </h3>
            </div>
            <div className="space-y-3">
              {(analytics.top_backup_destinations || []).length === 0 && (
                <AnalyticsEmptyState message={t('platformAnalytics.noData')} />
              )}
              {(analytics.top_backup_destinations || []).map((dest, idx) => (
                <div
                  key={`${dest.destination_name}-${idx}`}
                  className="flex items-center justify-between p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-black text-text-subtle-light dark:text-text-subtle-dark">
                      #{idx + 1}
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {dest.destination_name}
                      </p>
                      <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('platformAnalytics.organizationCount', {
                          count: dest.organization_count,
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-900 dark:text-white">
                      {formatNumber(dest.storage_used, i18n.resolvedLanguage || 'en', 2)} GB
                    </p>
                    <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark font-bold">
                      {formatPercent(dest.percentage, i18n.resolvedLanguage || 'en', 1)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Geographic Distribution */}
        {(analytics.geographic_distribution || []).length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                <Globe className="text-state-info-light dark:text-state-info-dark" />{' '}
                {t('platformAnalytics.geographicDistribution')}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(analytics.geographic_distribution || []).map((geo) => (
                <div
                  key={geo.country}
                  className="flex items-center justify-between p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl border border-surface-border-light dark:border-surface-border-dark"
                >
                  <div>
                    <p className="font-bold text-slate-900 dark:text-white">{geo.country}</p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                      {t('platformAnalytics.customerCount', { count: geo.customer_count })}
                    </p>
                  </div>
                  <Badge variant="info">
                    {formatPercent(geo.percentage, i18n.resolvedLanguage || 'en', 1)}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

function formatMonth(month: string, locale: string) {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;

  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatNumber(value: number, locale: string, maximumFractionDigits = 0) {
  return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);
}

function formatPercent(value: number, locale: string, maximumFractionDigits = 1) {
  return `${formatNumber(value, locale, maximumFractionDigits)}%`;
}

function formatFeatureName(feature: string, t: (key: string) => string) {
  const key = `platformAnalytics.featureNames.${feature
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return feature.replace(/[._]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function AnalyticsEmptyState({ message }: { message: string }) {
  return (
    <p className="rounded-xl border border-dashed border-surface-border-light dark:border-surface-border-dark px-4 py-8 text-center text-sm text-text-muted-light dark:text-text-muted-dark">
      {message}
    </p>
  );
}

function StatCard({ label, value, icon, color, bg }: any) {
  return (
    <div className={`min-w-0 p-6 rounded-2xl ${bg} flex items-start justify-between gap-3`}>
      <div className="min-w-0">
        <p className="break-all text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
          {value}
        </p>
        <p className={`text-xs font-bold uppercase tracking-wider ${color}`}>{label}</p>
      </div>
      <div
        className={`shrink-0 p-3 rounded-xl bg-surface-card-light/50 dark:bg-surface-card-dark/50 ${color}`}
      >
        {icon}
      </div>
    </div>
  );
}
